import React, { useMemo } from 'react';
import { GoodfyndSitesHeader } from '@features/goodfynd-sites/components/goodfynd-sites-header';
import {
  isGoodfyndSitesHost,
  isGoodfyndSitesPreview,
} from '@features/goodfynd-sites/utils/goodfynd-sites-util';
import type { UserProfileProps } from '@goodfynd/dashboard';
import { FlexView, Logo, Tag } from '@goodfynd/ui';
import { useRouter } from 'next/router';

import config from '../../../config';
import routes, { externalRoutes } from '../../../config/routes';
import strings from '../../../config/strings';
import { useApp } from '../../../context/app-context';
import { usePermissions } from '../../../hooks/usePermissions';
import { stringUtil } from '../../../utils';
import { useScreenMdSize } from '../../../utils/base';
import { Cart } from '../../checkout/Cart';
import { NavWrapper } from '../Nav';

import { MAIN_NAV } from './header-links';
import { HeaderUserProfile } from './HeaderUserProfile';
import { StyledContainer, StyledHeader, StyledNavRight } from './styles';
import type { HeaderProps } from './types';

export default function Header({
  children,
  fixed = true,
  navLinks = MAIN_NAV,
  shadow = true,
  showNav = true,
}: HeaderProps) {
  const router = useRouter();
  const { isOwner, isAdmin } = usePermissions();

  const collapseMenu = useScreenMdSize();

  const { isLoggedIn, user } = useApp();

  const userMenu = useMemo<UserProfileProps['options']>(() => {
    const items: UserProfileProps['options'] = [
      {
        hide: isLoggedIn,
        // icon: 'person',
        label: 'Login',
        onClick: () =>
          router.push(routes.account.signIn + `?returnUrl=${router.asPath}`),
      },
      {
        hide: !isLoggedIn,
        // icon: collapseMenu ? renderAvatar() : 'person_circled',
        label: 'View Profile',
        onClick: () => router.push(routes.account.home),
      },
    ];

    if (collapseMenu) {
      items.unshift(
        {
          label: strings.labels.findFood,
          onClick: () => router.push(routes.search.home),
        },
        {
          label: strings.labels.bookATruck,
          onClick: () => router.push(routes.events.home),
        },
        {
          divider: true,
        }
      );
    }

    if (isLoggedIn && (isOwner || isAdmin)) {
      const externalItems: UserProfileProps['options'] = [];

      if (isOwner || isAdmin) {
        externalItems.push({
          label: strings.labels.myBusinesses,
          onClick: () =>
            (window.location.href = user?.defaultVendorId
              ? stringUtil.replace(externalRoutes.vendors.myBusinesses, {
                  ':id': user?.defaultVendorId,
                })
              : externalRoutes.vendors.home),
        });
      }

      if (isOwner || isAdmin) {
        externalItems.push({
          label: strings.labels.myEvents,
          onClick: () =>
            (window.location.href = user?.defaultLotId
              ? stringUtil.replace(externalRoutes.events.myEvents, {
                  ':id': user?.defaultLotId,
                })
              : externalRoutes.events.home),
        });
      }

      externalItems.push({
        divider: true,
      });
      items.unshift(...externalItems);
    }

    return items;
  }, [
    isLoggedIn,
    collapseMenu,
    isOwner,
    isAdmin,
    router,
    user?.defaultVendorId,
    user?.defaultLotId,
  ]);

  const buildHeader = () => {
    const isGoodfyndSites = isGoodfyndSitesHost();
    const isPreview = isGoodfyndSitesPreview();
    return (
      <StyledContainer>
        <FlexView align="center" gap={8} wrap="nowrap">
          <Logo
            environment={config.env.NAME_LOWER}
            unoptimized
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.location.href = window.location.origin;
            }}
          />
          {isGoodfyndSites && <Tag type="positive">Ordering</Tag>}
          {isPreview && <Tag type="important">Preview</Tag>}
        </FlexView>

        {showNav && (
          <StyledNavRight>
            {!isGoodfyndSites && (
              <NavWrapper links={navLinks} variant="horizontal" />
            )}

            <Cart />

            {!isGoodfyndSites && <HeaderUserProfile options={userMenu} />}
          </StyledNavRight>
        )}
      </StyledContainer>
    );
  };

  const isGoodfyndSites = isGoodfyndSitesHost();
  if (isGoodfyndSites) {
    return <GoodfyndSitesHeader />;
  }

  return (
    <StyledHeader fixed={fixed} shadow={shadow}>
      {children ? children : buildHeader()}
    </StyledHeader>
  );
}
